import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { AddUnitUserRequest, UnitUserResponse, UpdateUnitUserRequest } from '@troia-v3/types';
import { unitsApi } from '../services/unitsApi';

const MEMBERS_QUERY_KEY = 'unit-members';
const UNITS_QUERY_KEY = 'units';

/**
 * Hook to fetch members assigned to a unit.
 *
 * `enabled` (default `true`) — condicionar a `isOpen` em drawers
 * (CLAUDE.md SEMPRE #10) evita disparar a query com o drawer fechado.
 */
export const useUnitUsers = (unitId: string | undefined, options?: { enabled?: boolean }) => {
  return useQuery<UnitUserResponse[]>({
    queryKey: [MEMBERS_QUERY_KEY, unitId],
    queryFn: () => unitsApi.listUsers(unitId!),
    enabled: !!unitId && (options?.enabled ?? true),
    staleTime: 30_000,
  });
};

/**
 * Hook to add a member (user) to a unit.
 */
export const useAddUnitUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ unitId, data }: { unitId: string; data: AddUnitUserRequest }) =>
      unitsApi.addUser(unitId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [MEMBERS_QUERY_KEY, variables.unitId] });
      queryClient.invalidateQueries({ queryKey: [UNITS_QUERY_KEY, variables.unitId] });
      toast.success('Membro adicionado com sucesso!');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Erro ao adicionar membro';
      toast.error(message);
    },
  });
};

/**
 * Hook to update a unit member (role/priority/status).
 */
export const useUpdateUnitUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      unitId,
      userId,
      data,
    }: {
      unitId: string;
      userId: string;
      data: UpdateUnitUserRequest;
    }) => unitsApi.updateUser(unitId, userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [MEMBERS_QUERY_KEY, variables.unitId] });
      queryClient.invalidateQueries({ queryKey: [UNITS_QUERY_KEY, variables.unitId] });
      toast.success('Membro atualizado com sucesso!');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Erro ao atualizar membro';
      toast.error(message);
    },
  });
};

/**
 * Hook to remove a member (user) from a unit.
 */
export const useRemoveUnitUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ unitId, userId }: { unitId: string; userId: string }) =>
      unitsApi.removeUser(unitId, userId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [MEMBERS_QUERY_KEY, variables.unitId] });
      queryClient.invalidateQueries({ queryKey: [UNITS_QUERY_KEY, variables.unitId] });
      toast.success('Membro removido com sucesso!');
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Erro ao remover membro';
      toast.error(message);
    },
  });
};
